<template>
  <div class="checkout-completion">
    <v-container grid-list-md>
      <v-row wrap justify="center" fill-height>
        <v-col cols="12" sm="12" md="12" lg="12" xl="12">
          <!-- Success Header -->
          <div class="text-center mb-10">
            <div class="success-icon-container mb-6">
              <v-icon class="success-icon" color="#4CAF50" size="64">mdi-check-circle</v-icon>
            </div>
            <h1 class="success-title mb-4">Enrollment complete!</h1>
            <p class="success-subtitle">
              Congratulations on enrolling in the course. Your payment has been processed successfully.
            </p>
          </div>

          <!-- Course Details Card -->
          <v-card class="enrollment-card mb-6" elevation="1">
            <v-card-text class="pa-7">
              <!-- Course Header Section -->
              <div class="course-header mb-4">
                <div class="course-code-badge">
                  {{ courseCode }}
                </div>
                <div class="course-title ml-3">{{ courseTitle }}</div>
                <UiBadge variant="success" rounded>Enrolled</UiBadge>
              </div>

              <!-- Course Details Section -->
              <div class="details-section">
                <v-row wrap class="course-details">
                  <v-col cols="6" sm="3" class="detail-field">
                    <div class="detail-label">START DATE</div>
                    <div class="detail-value">{{ startDate }}</div>
                  </v-col>
                  <v-col cols="6" sm="3" class="detail-field">
                    <div class="detail-label">END DATE</div>
                    <div class="detail-value">{{ endDate }}</div>
                  </v-col>
                  <v-col cols="6" sm="3" class="detail-field">
                    <div class="detail-label">CREDITS</div>
                    <div class="detail-value">{{ credits }} Credits</div>
                  </v-col>
                  <v-col cols="6" sm="3" class="detail-field">
                    <div class="detail-label">ORDER NUMBER</div>
                    <div class="detail-value">{{ orderNumber }}</div>
                  </v-col>
                </v-row>
              </div>

              <!-- Transaction Details Section -->
              <div class="details-section">
                <v-row wrap class="transaction-details">
                  <v-col cols="6" sm="4" class="detail-field">
                    <div class="detail-label">TRANSACTION ID</div>
                    <div class="detail-value">{{ transactionId }}</div>
                  </v-col>
                  <v-col cols="6" sm="4" class="detail-field">
                    <div class="detail-label">ORDER DATE</div>
                    <div class="detail-value">{{ orderDate }}</div>
                  </v-col>
                  <v-col cols="12" sm="4" class="detail-field">
                    <div class="detail-label">TOTAL AMOUNT</div>
                    <div class="detail-value amount-value">${{ totalAmount }}</div>
                  </v-col>
                </v-row>
              </div>

              <!-- Receipt Buttons Section -->
              <div class="details-section">
                <div class="receipt-buttons">
                  <UiButton variant="maroon" :block="$vuetify.display.xs" @click="downloadReceipt">
                    <template #startIcon>
                      <v-icon color="white" size="20">mdi-download</v-icon>
                    </template>
                    Download receipt
                  </UiButton>
                  <UiButton variant="maroon" :block="$vuetify.display.xs" class="ml-sm-3" @click="printReceipt">
                    <template #startIcon>
                      <v-icon color="white" size="20">mdi-printer</v-icon>
                    </template>
                    Print receipt
                  </UiButton>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Next Steps Section - Only show for new users without ASURITE ID -->
          <div class="next-steps-section mt-10" v-if="!hasAsuriteId">
            <h2 class="next-steps-title mb-8">Here's what's next:</h2>

            <div class="steps-container">
              <div class="step-wrapper">
                <div class="step-item">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <h3 class="step-title mt-0">Check your email</h3>
                    <p class="step-description">You will receive your ASURITE ID via email within 48 business hours.</p>
                  </div>
                </div>
                <div class="step-connector" />
              </div>

              <div class="step-wrapper">
                <div class="step-item">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <h3 class="step-title mt-0">Set up DUO</h3>
                    <p class="step-description">DUO protects your account by requiring a second verification step.</p>
                  </div>
                </div>
                <div class="step-connector" />
              </div>

              <div class="step-wrapper">
                <div class="step-item">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <h3 class="step-title mt-0">Begin your learning journey</h3>
                    <p class="step-description">Access your course on your dashboard.</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="support-section mt-5 text-left">
              <p class="support-text">
                Didn't receive your ASURITE ID in your email?
                <a href="#" class="support-link" @click="contactSupport">Contact support</a>
              </p>
            </div>
          </div>

          <!-- Existing Users Section - Show for users with ASURITE ID -->
          <div class="existing-user-section mt-10 text-center" v-else>
            <h2 class="existing-user-title mb-4">You're all set!</h2>
          </div>

          <!-- Return to Dashboard Button -->
          <div class="text-center mt-6">
            <UiButton variant="gold" size="medium" @click="returnToDashboard">
              {{ from === 'pathways' ? 'Return to Pathways' : 'Return to dashboard' }}
              <template #endIcon>
                <v-icon color="black" size="20">mdi-arrow-right</v-icon>
              </template>
            </UiButton>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { DataLayerMixin } from '../mixins/dataLayerMixin';
import { useUserStore } from '@/stores/user';
import { buildGa4EcommerceItem } from '@/utils/ga4Items';
import { getAttributionParams } from '@/util/param-persistence';
import { Button as UiButton, Badge as UiBadge } from './ui/index.js';

export default defineComponent({
  name: 'CourseCheckoutCompletion',

  mixins: [DataLayerMixin],

  components: {
    UiButton,
    UiBadge
  },

  data() {
    return {
      transactionData: null
    };
  },

  computed: {
    courseCode() {
      return this.transactionData?.courseCode || this.$route.params.courseCode;
    },
    courseTitle() {
      return this.transactionData?.courseTitle || this.$route.params.courseTitle;
    },
    startDate() {
      return this.transactionData?.startDate || this.$route.params.startDate;
    },
    endDate() {
      return this.transactionData?.endDate || this.$route.params.endDate;
    },
    courseOfferingId() {
      return this.transactionData?.courseOfferingId || this.$route.params.courseOfferingId || '';
    },
    canvasCourseId() {
      return this.transactionData?.canvasCourseId || this.$route.params.canvasCourseId || '';
    },
    courseStartDate() {
      return this.transactionData?.courseStartDate || this.$route.params.courseStartDate || '';
    },
    interestTrack() {
      return this.transactionData?.interestTrack || this.$route.params.interestTrack || '';
    },
    sessionName() {
      return this.transactionData?.sessionName || this.$route.params.sessionName || '';
    },
    courseType() {
      return this.transactionData?.courseType || this.$route.params.courseType || '';
    },
    credits() {
      return this.transactionData?.credits || this.$route.params.credits;
    },
    orderNumber() {
      return this.transactionData?.orderNumber || this.$route.params.orderNumber;
    },
    transactionId() {
      return this.transactionData?.transactionId || this.$route.params.transactionId;
    },
    orderDate() {
      return this.transactionData?.orderDate || this.$route.params.orderDate;
    },
    totalAmount() {
      // ?? not ||: a $0 benefits-covered receipt is a real amount, not a miss.
      return this.transactionData?.totalAmount ?? this.$route.params.totalAmount;
    },
    from() {
      return this.transactionData?.from || this.$route.params.from;
    },
    hasAsuriteId() {
      const userStore = useUserStore();
      return !!userStore.apis?.learnerData?._asuRite;
    }
  },

  created() {
    // Load transaction data from session storage or route params
    const sessionData = sessionStorage.getItem('checkoutTransactionData');
    if (sessionData) {
      this.transactionData = JSON.parse(sessionData);
    } else if (Object.keys(this.$route.params).length > 0) {
      this.transactionData = this.$route.params;
    }

    // If no transaction data available, redirect to dashboard
    if (!this.transactionData && !this.$route.params.courseCode) {
      this.$router.push('/dashboard');
    }
  },

  mounted() {
    // Send purchase confirmation event for GTM/Meta tracking
    if (this.transactionData) {
      const { campaign, partner } = getAttributionParams();

      // Standard purchase confirmation event
      const purchaseConfirmationEvent = {
        event: 'purchase_confirmation',
        campaign,
        partner,
        transaction_id: this.transactionId,
        order_number: this.orderNumber,
        value: parseFloat(this.totalAmount),
        currency: 'USD',
        course_code: this.courseCode,
        course_title: this.courseTitle,
        credits: this.credits,
        start_date: this.startDate,
        end_date: this.endDate
      };

      // Clear previous ecommerce object
      if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
      }

      const userStore = useUserStore();
      const leSfContactId = userStore.apis?.learnerData?._contactId || userStore.apis?.learnerData?.contactId || '';

      // Enhanced ecommerce purchase event
      const ecommercePurchaseEvent = {
        event: 'purchase',
        le_sf_contact_id: leSfContactId,
        campaign,
        partner,
        ecommerce: {
          transaction_id: this.transactionId,
          value: parseFloat(this.totalAmount),
          currency: 'USD',
          items: [
            buildGa4EcommerceItem({
              canvasCourseId: this.canvasCourseId,
              offeringId: this.courseOfferingId,
              courseName: this.courseTitle,
              coursePrefix: this.courseCode,
              interestTrack: this.interestTrack,
              sessionName: this.sessionName,
              // Not this.startDate — that is the receipt date, not the course start.
              startDate: this.courseStartDate,
              courseType: this.courseType,
              price: parseFloat(this.totalAmount)
            })
          ]
        }
      };

      // Push standard purchase confirmation event to dataLayer
      if (window.dataLayer) {
        window.dataLayer.push(purchaseConfirmationEvent);
      }

      // Push ecommerce purchase event using asuoDataLayerClick
      this.asuoDataLayerClick('ecommerceTags', '0', ecommercePurchaseEvent);

      // Clear transaction data from session storage to prevent duplicate events
      sessionStorage.removeItem('checkoutTransactionData');
    }
  },

  methods: {
    downloadReceipt() {
      // Create a receipt content similar to what would be printed
      const receiptContent = this.generateReceiptHTML();

      // Create a blob with the HTML content
      const blob = new Blob([receiptContent], { type: 'text/html' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${this.orderNumber}.html`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    generateReceiptHTML() {
      return `
<!DOCTYPE html>
<html>
<head>
    <title>Course Enrollment Receipt</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; }
        .header { text-align: center; margin-bottom: 40px; }
        .success-icon { color: #4CAF50; font-size: 48px; margin-bottom: 20px; }
        .title { font-size: 28px; font-weight: bold; margin-bottom: 10px; }
        .subtitle { color: #666; margin-bottom: 40px; }
        .receipt-box { border: 1px solid #ddd; padding: 30px; border-radius: 8px; }
        .course-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .course-code { background: #f1f1f1; padding: 8px 16px; border-radius: 4px; font-weight: bold; }
        .enrolled-badge { background: #4CAF50; color: white; padding: 6px 12px; border-radius: 16px; }
        .course-title { font-size: 20px; font-weight: bold; margin: 20px 0; }
        .details-section { border-top: 1px solid #eee; padding-top: 20px; margin-top: 20px; }
        .detail-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .detail-label { font-weight: bold; color: #666; text-transform: uppercase; font-size: 12px; }
        .detail-value { font-weight: bold; }
        .amount { color: #2ba206; font-size: 18px; }
        .footer { text-align: center; margin-top: 40px; color: #666; }
    </style>
</head>
<body>
    <div class="header">
        <div class="success-icon">✓</div>
        <h1 class="title">Enrollment Complete!</h1>
        <p class="subtitle">Congratulations on enrolling in the course. Your payment has been processed successfully.</p>
    </div>
    
    <div class="receipt-box">
        <div class="course-header">
            <span class="course-code">${this.courseCode}</span>
            <span class="enrolled-badge">Enrolled</span>
        </div>
        
        <h2 class="course-title">${this.courseTitle}</h2>
        
        <div class="details-section">
            <div class="detail-row">
                <span class="detail-label">Start Date:</span>
                <span class="detail-value">${this.startDate}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">End Date:</span>
                <span class="detail-value">${this.endDate}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Credits:</span>
                <span class="detail-value">${this.credits} Credits</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Order Number:</span>
                <span class="detail-value">${this.orderNumber}</span>
            </div>
        </div>
        
        <div class="details-section">
            <div class="detail-row">
                <span class="detail-label">Transaction ID:</span>
                <span class="detail-value">${this.transactionId}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Order Date:</span>
                <span class="detail-value">${this.orderDate}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Total Amount:</span>
                <span class="detail-value amount">$${this.totalAmount}</span>
            </div>
        </div>
    </div>
    
    <div class="footer">
        <p>Arizona State University - Universal Learner Courses</p>
        <p>Receipt generated on ${new Date().toLocaleDateString()}</p>
    </div>
</body>
</html>`;
    },

    printReceipt() {
      window.print();
    },

    contactSupport() {
      // Implementation for contacting support
      // TODO: Implement contact support functionality
    },

    returnToDashboard() {
      // Clear session storage when returning to dashboard
      sessionStorage.removeItem('checkoutTransactionData');

      // Always return to dashboard with refresh param
      // The pathways modal is shown on the dashboard, not a separate route
      this.$router.push('/dashboard?refreshEnrollments=true');
    }
  }
});
</script>

<style lang="stylus" scoped>
.checkout-completion {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 40px 0;
}

.success-title {
  font-size: 32px;
  font-weight: 600;
  color: #333;
}

.success-subtitle {
  font-size: 16px;
  color: #666;
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.5;
}

.enrollment-card {
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

.course-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.course-header .course-code-badge {
  order: 1;
}

.course-header .course-title {
  order: 2;
  flex: 1;
}

.course-header .ui-badge {
  order: 3;
  margin-left: auto;
}

.course-code-badge {
  background-color: #f1f1f1;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.course-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.details-section {
  border-top: 1px solid #e8e8e8;
  padding-top: 16px;
  margin-top: 16px;
}

.course-details, .transaction-details {
  padding-top: 0;
}

.detail-field {
  padding: 12px 16px;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.amount-value {
  color: #2ba206;
  font-size: 16px;
}

.receipt-buttons {
  padding-top: 0;
}

.next-steps-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.existing-user-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.existing-user-description {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.steps-container {
  position: relative;
}

.step-wrapper {
  position: relative;
}

.step-item {
  display: flex;
  align-items: flex-start;
  position: relative;
  z-index: 2;
  margin-bottom: 10px;
}

.step-number {
  width: 40px;
  height: 40px;
  background-color: #FEC627;
  color: #333;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  margin-right: 24px;
  flex-shrink: 0;
  position: relative;
  z-index: 3;
}

.step-connector {
  width: 2px;
  height: calc(100% + 10px);
  background-color: #FEC627;
  position: absolute;
  left: 19px;
  top: 40px;
  z-index: 1;
}

.step-wrapper:last-child .step-connector {
  display: none;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.step-description {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.support-section {
  text-align: center;
}

.support-text {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.support-link {
  color: #8C1D40;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
}

@media (max-width: 600px) {
  .checkout-completion {
    padding: 20px 0;
  }

  .success-title {
    font-size: 28px;
  }

  .success-subtitle {
    font-size: 14px;
  }

  .course-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .receipt-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .step-number {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .step-connector {
    left: 17px;
    top: 36px;
    height: calc(100% + 10px);
  }

  .step-title {
    font-size: 16px;
  }

  .step-description {
    font-size: 14px;
  }
}
</style>
